import { useState } from "react";
import { VISUALLY_HIDDEN } from "../visuallyHidden";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import { ActionMenu } from "../ActionMenu";
import { useNextT } from "../../i18n";
import { usePhone } from "../usePhone";
import s from "./SelectionBar.module.scss";

export type SelectionAction = { id: string; label: string; danger?: boolean; onClick: () => void };
type Props = {
  count: number; countLabel: (n: number) => string; actions: SelectionAction[];
  onClear: () => void; clearLabel: string; label: string;
};

/** Always-mounted hidden live region: mount it permanently on the page so the first "1 selected" is announced (a bar that appears late is not). */
export const SelectionAnnouncer = ({ count, countLabel }: { count: number; countLabel: (n: number) => string }) => (
  <div role="status" aria-live="polite" className={VISUALLY_HIDDEN}>{count > 0 ? countLabel(count) : ""}</div>
);

/**
 * The dark sticky bar (stays at the bottom of its container) shown while rows are selected. Its count is plain text: pages must mount <SelectionAnnouncer> (the single live region). It never traps focus and has no Escape handler.
 * On phones: the count, the danger action and a ⋯ with the rest.
 */
export const SelectionBar = ({ count, countLabel, actions, onClear, clearLabel, label }: Props) => {
  const { t } = useNextT();
  const phone = usePhone();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const danger = actions.filter((a) => a.danger);
  const rest = actions.filter((a) => !a.danger);
  const shown = phone ? danger : actions;
  const more = phone && rest.length > 0;
  return (
    <>
      <div role="region" aria-label={label} className={phone ? `${s.bar} ${s.phone}` : s.bar}>
        <span className={s.count}>{countLabel(count)}</span>
        <div className={s.grow} />
        {shown.map((a) => (
          <button key={a.id} type="button" className={a.danger ? `${s.btn} ${s.danger}` : s.btn} onClick={a.onClick}>{a.label}</button>
        ))}
        {more && (
          <button type="button" className={`${s.btn} ${s.icon}`} aria-label={t("ui.more_actions")} aria-haspopup="menu" aria-expanded={!!anchor} onClick={(e) => setAnchor(e.currentTarget)}>
            <MoreHorizRoundedIcon aria-hidden />
          </button>
        )}
        <button type="button" className={`${s.btn} ${s.icon}`} aria-label={clearLabel} onClick={onClear}><CloseRoundedIcon aria-hidden /></button>
      </div>
      {more && (
        <ActionMenu label={label} anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} cancelLabel={t("ui.cancel")}
          items={rest.map((a) => ({ id: a.id, label: a.label, onSelect: a.onClick }))} />
      )}
    </>
  );
};
